<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { ref } from "vue";
import CloseButton from "./CloseButton.vue";
import { Button } from "@konigle/pixel/components/button";

type ConfirmButton = {
  type?: "neutral" | "danger" | "primary";
  text: string;
  onClick?: (formData?: any) => void;
};

type ConfirmFormInput = {
  name: string;
  type: "text" | "number" | "checkbox";
  label?: string;
  placeholder?: string;
  prompt?: string;
  tip?: string;
};

export type ConfirmPopupOptions = {
  title?: string;
  message?: string;
  buttons?: ConfirmButton[];
  closable?: boolean;
  formInputs?: ConfirmFormInput[];
};

const isOpen = ref<boolean>(false);
const form = ref<HTMLElement | null>(null);
const state = ref<ConfirmPopupOptions>({
  buttons: [],
  closable: false,
  formInputs: [],
});

function close() {
  isOpen.value = false;
}

function reset() {
  state.value = {
    buttons: [],
    formInputs: [],
    closable: false,
    title: "",
    message: "",
  };
}

function handleClick(btn: ConfirmButton) {
  if (btn.onClick) {
    if (state.value.formInputs?.length && form.value) {
      let formData: Record<string, any> = {};
      const inputs = form.value.querySelectorAll("input");
      inputs.forEach((input) => {
        formData[input.name] = input.value;
      });
      btn.onClick(formData);
    } else {
      btn.onClick();
    }
  }
  close();
}

function getButtonStyle(type: ConfirmButton["type"]) {
  switch (type) {
    case "danger":
      return "text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-700";
    case "primary":
      return "bg-primary-50 text-primary hover:bg-primary-100";
    default:
      return "text-gray-700 bg-gray-50 hover:bg-gray-100 hover:text-gray-900";
  }
}

defineExpose({
  alert: (options: ConfirmPopupOptions) => {
    reset();
    state.value = {
      ...state.value,
      ...options,
    };
    isOpen.value = true;
  },
});
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center"
        >
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-md bg-white p-6 text-left align-middle shadow-xl transition-all"
            >
              <CloseButton
                class="absolute top-1 right-1"
                @click="close"
                v-if="state.closable"
              />
              <DialogTitle
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900"
                v-if="state.title"
              >
                {{ state.title }}
              </DialogTitle>
              <div class="mt-2">
                <template v-if="state.message">
                  <p v-html="state.message" class="text-sm text-gray-500"></p>
                </template>
              </div>

              <template v-if="state.formInputs?.length">
                <div class="mt-4 space" ref="form">
                  <template v-for="input in state.formInputs">
                    <p v-if="input.prompt" class="text-sm text-gray-500">
                      {{ input.prompt }}
                    </p>
                    <label
                      v-if="input.label"
                      :for="input.name"
                      class="block text-sm font-medium text-gray-700"
                    >
                      {{ input.label }}
                    </label>
                    <input
                      v-if="input.type === 'text' || input.type === 'number'"
                      :type="input.type"
                      :name="input.name"
                      :placeholder="input.placeholder"
                      class="mt-1 input w-full"
                    />
                    <input
                      v-else-if="input.type === 'checkbox'"
                      :name="input.name"
                      class="mt-1 block h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary-500"
                    />
                    <p v-if="input.tip" class="mt-2 text-sm text-gray-500">
                      {{ input.tip }}
                    </p>
                  </template>
                </div>
              </template>

              <div class="mt-5 sm:mt-4 sm:flex justify-end gap-2">
                <Button
                  :variant="btn.type === 'neutral' ? 'default' : btn.type"
                  v-for="btn in state.buttons"
                  @click="handleClick(btn)"
                >
                  {{ btn.text }}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
